import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const MAX_DEVICES = 2;
const DEVICE_ID_KEY = "hockey_device_id";

// Generate a unique device ID
const generateDeviceId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

// Get or create device ID for this browser
const getDeviceId = (): string => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

// Get a readable device name
const getDeviceName = (): string => {
  const ua = navigator.userAgent;
  let browser = "Unknown Browser";
  let os = "Unknown OS";

  if (ua.includes("Chrome")) browser = "Chrome";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Safari")) browser = "Safari";
  else if (ua.includes("Edge")) browser = "Edge";

  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

  return `${browser} on ${os}`;
};

export interface DeviceSession {
  id: string;
  device_id: string;
  device_name: string | null;
  last_active_at: string;
  created_at: string;
}

export const useDeviceSession = () => {
  const deviceId = getDeviceId();
  const deviceName = getDeviceName();

  // Check if user can register this device (max 2 devices)
  const canRegisterDevice = useCallback(async (userId: string): Promise<{
    allowed: boolean;
    existingSessions: DeviceSession[];
    isCurrentDeviceRegistered: boolean;
  }> => {
    const { data: sessions, error } = await supabase
      .from("user_sessions")
      .select("*")
      .eq("user_id", userId)
      .order("last_active_at", { ascending: false });

    if (error) {
      console.error("Error checking device sessions:", error);
      return { allowed: false, existingSessions: [], isCurrentDeviceRegistered: false };
    }

    const existingSessions = (sessions || []) as DeviceSession[];
    const isCurrentDeviceRegistered = existingSessions.some(s => s.device_id === deviceId);

    // If current device is already registered, allow
    if (isCurrentDeviceRegistered) {
      return { allowed: true, existingSessions, isCurrentDeviceRegistered: true };
    }

    // If less than max devices, allow
    if (existingSessions.length < MAX_DEVICES) {
      return { allowed: true, existingSessions, isCurrentDeviceRegistered: false };
    }

    // Max devices reached
    return { allowed: false, existingSessions, isCurrentDeviceRegistered: false };
  }, [deviceId]);

  // Register this device for the user
  const registerDevice = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("user_sessions")
        .upsert({
          user_id: userId,
          device_id: deviceId,
          device_name: deviceName,
          last_active_at: new Date().toISOString(),
        }, {
          onConflict: "user_id,device_id",
        });

      if (error) {
        console.error("Error registering device:", error);
        return false;
      }
      return true;
    } catch (err) {
      console.error("Error registering device:", err);
      return false;
    }
  }, [deviceId, deviceName]);

  // Update last active timestamp
  const updateLastActive = useCallback(async (userId: string): Promise<void> => {
    await supabase
      .from("user_sessions")
      .update({ last_active_at: new Date().toISOString() })
      .eq("user_id", userId)
      .eq("device_id", deviceId);
  }, [deviceId]);

  // Remove a device session
  const removeDevice = useCallback(async (sessionId: string): Promise<boolean> => {
    const { error } = await supabase
      .from("user_sessions")
      .delete()
      .eq("id", sessionId);

    if (error) {
      console.error("Error removing device:", error);
      return false;
    }
    return true;
  }, []);

  // Remove current device (on logout)
  const removeCurrentDevice = useCallback(async (userId: string): Promise<void> => {
    await supabase
      .from("user_sessions")
      .delete()
      .eq("user_id", userId)
      .eq("device_id", deviceId);
  }, [deviceId]);

  return {
    deviceId,
    deviceName,
    canRegisterDevice,
    registerDevice,
    updateLastActive,
    removeDevice,
    removeCurrentDevice,
  };
};
